import { useState, useRef, useCallback, useEffect } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

export interface UseQRScannerResult {
  scannerRef: React.RefObject<HTMLDivElement>;
  isActive: boolean;
  isLoading: boolean;
  error: string | null;
  startScanner: () => Promise<void>;
  stopScanner: () => void;
}

interface UseQRScannerOptions {
  onScanSuccess: (decodedText: string) => void;
  onScanError?: (error: string) => void;
}

export const useQRScanner = ({ onScanSuccess, onScanError }: UseQRScannerOptions): UseQRScannerResult => {
  const scannerRef = useRef<HTMLDivElement>(null);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        if (html5QrCodeRef.current.isScanning) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
      html5QrCodeRef.current = null;
    }
    setIsActive(false);
    setError(null);
  }, []);

  const startScanner = useCallback(async () => {
    if (!scannerRef.current) return;
    
    setIsLoading(true);
    setError(null);

    try {
      // Stop any existing scanner first
      await stopScanner();

      const scannerId = 'qr-scanner-' + Date.now();
      scannerRef.current.id = scannerId;

      const html5QrCode = new Html5Qrcode(scannerId);
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          // On successful scan
          onScanSuccess(decodedText);
        },
        () => {
          // Ignore scan errors (no QR code found yet)
        }
      );

      setIsActive(true);
    } catch (err) {
      console.error('QR Scanner error:', err);
      
      let errorMessage = 'Kunde inte starta QR-skannern';
      
      if (err instanceof Error) {
        if (err.message.includes('NotAllowedError') || err.message.includes('Permission')) {
          errorMessage = 'Kameratillgång nekad. Tillåt kameran i webbläsarens inställningar.';
        } else if (err.message.includes('NotFoundError') || err.message.includes('not found')) {
          errorMessage = 'Ingen kamera hittades på enheten.';
        } else if (err.message.includes('NotReadableError') || err.message.includes('in use')) {
          errorMessage = 'Kameran används av en annan applikation.';
        }
      }
      
      setError(errorMessage);
      onScanError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [onScanSuccess, onScanError, stopScanner]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  return {
    scannerRef,
    isActive,
    isLoading,
    error,
    startScanner,
    stopScanner
  };
};
